import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import FeatherIcon from "feather-icons-react";

export const KIND_META = {
  MOVE_IN: { label: "Move-in", cls: "k-movein" },
  MISSING: { label: "Missing", cls: "k-missing" },
  DOCUMENT_ONLY: { label: "Doc only", cls: "k-doc" },
  RESUME_DUE: { label: "Resume", cls: "k-resume" },
  UNVERIFIED: { label: "Verify", cls: "k-verify" },
};
const KIND_FILTER_LABEL = {
  MOVE_IN: "Move-ins without PAD",
  MISSING: "PAD info missing",
  DOCUMENT_ONLY: "Document only",
  RESUME_DUE: "Temporary stops ending",
  UNVERIFIED: "Awaiting verification",
};
const PAGE = 120;

export const relTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
};

const Row = ({ item, onGo }) => {
  const k = KIND_META[item.kind] || { label: item.kind, cls: "" };
  return (
    <button type="button" className="nc-row" onClick={() => onGo(item.link)} title="Open this unit's PAD Info">
      <span className={`nc-dot ${item.severity}`} aria-hidden="true" />
      <span className="nc-body">
        <span className="nc-line1">
          <span className="nc-unitpill">{item.unit_no}</span>
          <span className="nc-name">{item.tenant || <em>vacant</em>}</span>
          <span className={`nc-kind ${k.cls}`}>{k.label}</span>
        </span>
        <span className="nc-title">{item.title}</span>
        {item.detail ? <span className="nc-detail">{item.detail}</span> : null}
      </span>
      <span className="nc-go" aria-hidden="true"><FeatherIcon icon="chevron-right" size={16} /></span>
    </button>
  );
};

/**
 * The open panel: two tabs — live "PAD pending" work (never dismissable; rows leave when the
 * data changes) and stored "Notifications" (per-user read/unread).
 */
const NotificationPanel = ({ pending, notifications, loading, error, lastLoaded, onRefresh, onClose, onMarkRead, onMarkAllRead, viewer }) => {
  const history = useHistory();
  const [tab, setTab] = useState(pending.counts.total === 0 && notifications.unread > 0 ? "notes" : "pad");
  const [kind, setKind] = useState("");
  const [property, setProperty] = useState("");
  const [limit, setLimit] = useState(PAGE);

  const properties = useMemo(() => {
    const m = new Map();
    for (const i of pending.items) {
      const cur = m.get(i.property_id) || { id: i.property_id, name: i.property_name || "Property", n: 0 };
      cur.n += 1;
      m.set(i.property_id, cur);
    }
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [pending.items]);

  const kinds = useMemo(() => {
    const m = new Map();
    for (const i of pending.items) m.set(i.kind, (m.get(i.kind) || 0) + 1);
    return ["MOVE_IN", "MISSING", "DOCUMENT_ONLY", "RESUME_DUE", "UNVERIFIED"].filter((k) => m.has(k)).map((k) => [k, m.get(k)]);
  }, [pending.items]);

  const filtered = useMemo(
    () => pending.items.filter((i) => (!property || i.property_id === property) && (!kind || i.kind === kind)),
    [pending.items, property, kind]
  );

  const grouped = useMemo(() => {
    const out = [];
    let cur = null;
    for (const i of filtered.slice(0, limit)) {
      const key = `${i.property_name}|${i.building_name}`;
      if (!cur || cur.key !== key) {
        cur = { key, property: i.property_name || "Property", building: i.building_name || "", items: [] };
        out.push(cur);
      }
      cur.items.push(i);
    }
    return out;
  }, [filtered, limit]);

  const go = (link) => {
    if (!link) return;
    onClose && onClose();
    // Same-route deep links must still re-run the effect: make each click's search string unique.
    history.push(link + (link.includes("?") ? "&" : "?") + "t=" + Date.now());
  };

  const scopeLabel = viewer?.role === "auditor" ? "Accounting view" : viewer?.role === "company" ? "All properties" : "Your properties";

  return (
    <div className="nc-panel" role="dialog" aria-label="Notification center">
      <div className="nc-head">
        <span className="nc-head-icon"><FeatherIcon icon="bell" size={16} /></span>
        <span className="nc-title-wrap">
          <span className="nc-h1">Pending actions</span>
          <span className="nc-h2">{scopeLabel}{lastLoaded ? ` · updated ${relTime(lastLoaded)}` : ""}</span>
        </span>
        <button type="button" className={`nc-iconbtn${loading ? " spin" : ""}`} title="Refresh now" onClick={() => onRefresh(true)} disabled={loading}>
          <FeatherIcon icon="refresh-cw" size={14} />
        </button>
        <button type="button" className="nc-iconbtn" title="Close" onClick={onClose}>
          <FeatherIcon icon="x" size={16} />
        </button>
      </div>

      <div className="nc-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "pad"} className={tab === "pad" ? "on" : ""} onClick={() => setTab("pad")}>
          <FeatherIcon icon="credit-card" size={13} /> PAD pending <span className="n">{pending.counts.total}</span>
        </button>
        <button type="button" role="tab" aria-selected={tab === "notes"} className={tab === "notes" ? "on" : ""} onClick={() => setTab("notes")}>
          <FeatherIcon icon="inbox" size={13} /> Notifications <span className="n">{notifications.unread}</span>
        </button>
      </div>

      {error ? <div className="nc-error">{error}</div> : null}

      {tab === "pad" ? (
        <div className="nc-tabpane">
          {(properties.length > 1 || kinds.length > 1) && (
            <div className="nc-toolbar">
              <label className="nc-select">
                <FeatherIcon icon="filter" size={12} />
                <select value={kind} onChange={(e) => { setKind(e.target.value); setLimit(PAGE); }} aria-label="Filter by type">
                  <option value="">All types ({pending.counts.total})</option>
                  {kinds.map(([k, n]) => (
                    <option key={k} value={k}>{KIND_FILTER_LABEL[k] || k} ({n})</option>
                  ))}
                </select>
              </label>
              {properties.length > 1 && (
                <label className="nc-select">
                  <FeatherIcon icon="map-pin" size={12} />
                  <select value={property} onChange={(e) => { setProperty(e.target.value); setLimit(PAGE); }} aria-label="Filter by property">
                    <option value="">All properties</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({p.n})</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          <div className="nc-list">
            {filtered.length === 0 ? (
              <div className="nc-empty">
                <span className="nc-empty-icon"><FeatherIcon icon="check-circle" size={22} /></span>
                <span className="nc-empty-title">All caught up</span>
                <span className="nc-empty-text">
                  {pending.items.length ? "Nothing matches the current filters." : "Every occupied unit has PAD info and no move-in is waiting."}
                </span>
              </div>
            ) : (
              <>
                {grouped.map((g) => (
                  <section key={g.key} className="nc-section">
                    <div className="nc-group">
                      <span className="nc-group-name">{g.property}</span>
                      {g.building ? <span className="nc-group-bld">{g.building}</span> : null}
                      <span className="nc-group-n">{g.items.length}</span>
                    </div>
                    {g.items.map((i) => <Row key={i.id} item={i} onGo={go} />)}
                  </section>
                ))}
                {filtered.length > limit && (
                  <button type="button" className="nc-more" onClick={() => setLimit((l) => l + PAGE)}>
                    Show {Math.min(PAGE, filtered.length - limit)} more · {filtered.length - limit} remaining
                  </button>
                )}
              </>
            )}
          </div>

          <div className="nc-foot">
            <span className="nc-legend">
              <span><i className="nc-dot danger" />{pending.counts.danger} urgent</span>
              <span><i className="nc-dot warning" />{pending.counts.warning} action</span>
              <span><i className="nc-dot info" />{pending.counts.info} info</span>
            </span>
            <span className="nc-foot-hint" title="Rows disappear on their own once the PAD info is recorded.">Clears when PAD info is recorded</span>
          </div>
        </div>
      ) : (
        <div className="nc-tabpane">
          <div className="nc-list">
            {notifications.items.length === 0 ? (
              <div className="nc-empty">
                <span className="nc-empty-icon"><FeatherIcon icon="inbox" size={22} /></span>
                <span className="nc-empty-title">No notifications</span>
                <span className="nc-empty-text">Events like “New PAD recorded — please verify” and “Accounting verified…” show up here.</span>
              </div>
            ) : (
              notifications.items.map((n) => (
                <button
                  key={n._id}
                  type="button"
                  className={`nc-row nc-note${n.read ? " read" : ""}`}
                  onClick={() => {
                    if (!n.read) onMarkRead(n._id);
                    if (n.link) go(n.link);
                  }}
                  title={n.link ? "Open" : "Mark as read"}
                >
                  <span className={`nc-dot ${n.read ? "read" : n.severity || "info"}`} aria-hidden="true" />
                  <span className="nc-body">
                    <span className="nc-title">{n.title}</span>
                    {n.message ? <span className="nc-detail">{n.message}</span> : null}
                    <span className="nc-meta">{n.created_by?.name ? `${n.created_by.name} · ` : ""}{relTime(n.createdAt)}</span>
                  </span>
                  <span className="nc-go" aria-hidden="true">{n.link ? <FeatherIcon icon="chevron-right" size={16} /> : null}</span>
                </button>
              ))
            )}
          </div>
          <div className="nc-foot">
            <span>{notifications.unread} unread</span>
            <button type="button" className="nc-linkbtn" onClick={onMarkAllRead} disabled={!notifications.unread}>Mark all as read</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
